import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { loginWithEmail, registerWithEmail, loginWithGoogle, sendResetEmail, clearAuthError } from "../redux/authSlice";

type Mode = "login" | "register";

export default function Authentication() {
  const dispatch = useDispatch<AppDispatch>();
  const { status, error } = useSelector((s: RootState) => s.auth);

  const [mode, setMode] = useState<Mode>("login");
  const [lampOn, setLampOn] = useState(false);
  const [pulled, setPulled] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [note, setNote] = useState("");
  const busy = status === "loading";

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "login") dispatch(loginWithEmail({ email, password }));
    else dispatch(registerWithEmail({ email, password, firstName: first, lastName: last }));
  };

  const switchMode = (m: Mode) => {
    if (m === mode) return;
    dispatch(clearAuthError());
    setNote("");
    setMode(m);
  };

  const pullChain = () => {
    setPulled(true);
    setLampOn((v) => !v);
    window.setTimeout(() => setPulled(false), 320);
  };

  const forgot = async () => {
    if (!email) return setNote("Type your email first.");
    await dispatch(sendResetEmail(email));
    setNote("Reset email sent. Check your inbox.");
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500&family=Orbitron:wght@900&display=swap');

    .au-root{
      --bg:#000;--fg:#fff;--muted:rgba(255,255,255,.55);--line:rgba(255,255,255,.35);
      --card:rgba(0,0,0,.72);--red:#e10000;--red-d:#b30000;
      position:relative;width:100%;max-width:100vw;min-height:100dvh;overflow-x:hidden;
      display:flex;flex-direction:column;align-items:center;
      padding:0 16px 48px;box-sizing:border-box;
      background:var(--bg);color:var(--fg);font-family:'Jost',sans-serif;
      transition:background .6s ease,color .6s ease;
    }
    .au-root.on{
      --bg:#fff;--fg:#000;--muted:rgba(0,0,0,.55);--line:rgba(0,0,0,.35);
      --card:rgba(255,255,255,.82);
      background:radial-gradient(ellipse 80% 60% at 50% 0%,#fff3b0 0%,#fff9d6 38%,#ffffff 78%);
    }
    .au-root *{box-sizing:border-box}

    .au-spark{position:absolute;z-index:1;color:var(--fg);opacity:.7;animation:auTwinkle 3s ease-in-out infinite;pointer-events:none;transition:color .6s}
    @keyframes auTwinkle{0%,100%{opacity:.15;transform:scale(.7)}50%{opacity:.9;transform:scale(1.15)}}

    /* lamp */
    .au-lampwrap{position:relative;z-index:3;width:130px;height:162px;flex:none}
    .au-lamp{position:relative;z-index:3;display:block;width:130px;height:162px;padding:0;border:none;background:none;cursor:pointer;transform-origin:50% 0;animation:auSwing 6s ease-in-out infinite}
    .au-lamp:focus-visible{outline:1px solid var(--red);outline-offset:4px}
    .au-lamp svg{display:block;width:100%;height:100%;overflow:visible}
    .au-lamp svg{filter:drop-shadow(0 10px 8px rgba(0,0,0,.5));transition:filter .6s}
    .au-root.on .au-lamp svg{filter:drop-shadow(0 10px 8px rgba(110,0,0,.35))}
    .au-lamp:hover svg{filter:drop-shadow(0 10px 8px rgba(0,0,0,.5)) brightness(1.08)}
    .au-root.on .au-lamp:hover svg{filter:drop-shadow(0 10px 8px rgba(110,0,0,.35)) brightness(1.08)}
    .au-x{transition:opacity .55s ease}
    .au-when-on{opacity:0}
    .au-root.on .au-when-on{opacity:1}
    .au-root.on .au-when-off{opacity:0}
    .au-glow{opacity:0;transition:opacity .55s ease;filter:blur(7px)}
    .au-root.on .au-glow{opacity:.9}
    .au-chain{transition:transform .14s ease-out}
    .au-chain.pulled{transform:translateY(12px)}
    @keyframes auSwing{0%,100%{transform:rotate(-1.6deg)}50%{transform:rotate(1.6deg)}}

    .au-cone{position:absolute;z-index:0;top:100px;left:50%;width:min(980px,170vw);height:78vh;transform:translateX(-50%);
      background:linear-gradient(to bottom,rgba(255,226,110,.6),rgba(255,240,170,.18) 55%,rgba(255,244,190,0));
      clip-path:polygon(47% 0,53% 0,100% 100%,0 100%);opacity:0;transition:opacity .6s ease;pointer-events:none}
    .au-root.on .au-cone{opacity:1}

    .au-hint{position:relative;z-index:3;margin:6px 0 22px;letter-spacing:.4em;font-size:11px;font-family:'Cormorant Garamond',serif;font-weight:600;color:var(--muted);transition:color .6s}

    /* card */
    .au-card{position:relative;z-index:2;width:min(440px,100%);border:1px solid var(--fg);border-radius:4px;background:var(--card);
      backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);padding:28px 28px 30px;display:flex;flex-direction:column;gap:14px;
      transition:border-color .6s,background .6s}

    .au-toggle{position:relative;display:grid;grid-template-columns:1fr 1fr;border:1px solid var(--fg);border-radius:2px;margin-bottom:10px;overflow:hidden;transition:border-color .6s}
    .au-ind{position:absolute;top:0;bottom:0;left:0;width:50%;background:var(--fg);transition:transform .4s cubic-bezier(.22,.8,.26,1),background .6s}
    .au-toggle.reg .au-ind{transform:translateX(100%)}
    .au-tab{position:relative;z-index:1;background:none;border:none;cursor:pointer;padding:14px 8px;letter-spacing:.28em;font-size:12px;
      font-family:'Cormorant Garamond',serif;font-weight:600;color:var(--fg);transition:color .3s}
    .au-tab.active{color:var(--bg)}
    .au-tab:not(.active):hover{color:var(--red)}
    .au-tab:focus-visible{outline:1px solid var(--red);outline-offset:-4px}

    .au-title{font-family:'Orbitron','Arial Black',sans-serif;font-weight:900;text-transform:uppercase;font-size:clamp(20px,6vw,28px);
      line-height:1.15;letter-spacing:.04em;margin:6px 0 4px;color:#ff0000;font-synthesis:none}
    .au-sub{margin:0 0 6px;font-family:'Cormorant Garamond',serif;font-weight:500;letter-spacing:.18em;font-size:14px;color:var(--muted);transition:color .6s}

    .au-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;animation:auFade .35s ease}
    @keyframes auFade{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}

    .au-input{width:100%;background:transparent;color:var(--fg);border:1px solid var(--line);border-radius:2px;padding:14px 14px;
      font-family:'Jost',sans-serif;font-size:14px;font-weight:400;letter-spacing:.06em;outline:none;transition:border-color .25s,color .6s}
    .au-input::placeholder{color:var(--muted);letter-spacing:.12em}
    .au-input:hover{border-color:var(--fg)}
    .au-input:focus{border-color:var(--red)}

    .au-btn{width:100%;margin-top:6px;border:1px solid var(--fg);border-radius:999px;padding:14px 34px;background:var(--fg);color:var(--bg);
      font-family:'Jost',sans-serif;font-size:13px;letter-spacing:.25em;font-weight:500;cursor:pointer;transition:all .25s}
    .au-btn:hover:not(:disabled){background:var(--red);border-color:var(--red);color:#fff;transform:translateY(-3px) scale(1.02)}
    .au-btn:active:not(:disabled){background:var(--red-d);border-color:var(--red-d)}
    .au-btn:disabled{opacity:.5;cursor:default}
    .au-btn.ghost{background:transparent;color:var(--fg);margin-top:0}
    .au-btn:focus-visible{outline:1px solid var(--red);outline-offset:3px}

    .au-links{display:flex;justify-content:center;flex-wrap:wrap;gap:8px 22px;margin-top:6px}
    .au-link{background:none;border:none;padding:4px 0;cursor:pointer;color:var(--muted);letter-spacing:.2em;font-size:11px;
      font-family:'Cormorant Garamond',serif;font-weight:600;transition:color .25s}
    .au-link:hover{color:var(--red)}

    .au-error{margin:0;color:var(--red);font-size:13px;letter-spacing:.06em}
    .au-note{margin:0;color:var(--muted);font-size:13px;letter-spacing:.06em}

    @media(prefers-reduced-motion:reduce){
      .au-lamp,.au-spark{animation:none}
      .au-root,.au-ind,.au-cone,.au-chain{transition:none}
    }
    @media(max-width:420px){
      .au-card{padding:22px 18px 24px}
      .au-row{grid-template-columns:1fr}
    }
  `;

  return (
    <div className={`au-root ${lampOn ? "on" : ""}`}>
      <style>{css}</style>

      <span className="au-spark" style={{ top: "22%", left: "12%", fontSize: 18 }}>✦</span>
      <span className="au-spark" style={{ top: "40%", right: "10%", fontSize: 14, animationDelay: "1s" }}>✦</span>
      <span className="au-spark" style={{ top: "62%", left: "8%", fontSize: 12, animationDelay: "1.8s" }}>✧</span>
      <span className="au-spark" style={{ top: "18%", right: "22%", fontSize: 12, animationDelay: "2.3s" }}>✧</span>

      <div className="au-lampwrap">
        <div className="au-cone" aria-hidden="true" />
        <button
          type="button"
          className="au-lamp"
          onClick={pullChain}
          aria-pressed={lampOn}
          aria-label={lampOn ? "Turn the light off" : "Turn the light on"}
        >
          <svg viewBox="0 0 160 200" aria-hidden="true">
            <defs>
              <radialGradient id="au-pearl" cx="35%" cy="30%" r="75%">
                <stop offset="0" stopColor="#ffffff" />
                <stop offset=".35" stopColor="#fbf6f4" />
                <stop offset=".7" stopColor="#e3d9dc" />
                <stop offset="1" stopColor="#a99fa6" />
              </radialGradient>
              <pattern id="au-pearls" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
                <circle cx="4" cy="4" r="3.6" fill="url(#au-pearl)" />
                <circle cx="3" cy="2.8" r="1" fill="#fff" opacity=".95" />
              </pattern>
              <linearGradient id="au-white" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#b9b9c1" />
                <stop offset=".16" stopColor="#ffffff" />
                <stop offset=".34" stopColor="#f6f6f8" />
                <stop offset=".72" stopColor="#d6d6dc" />
                <stop offset="1" stopColor="#8d8d97" />
              </linearGradient>
              <linearGradient id="au-redg" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#600000" />
                <stop offset=".16" stopColor="#ff5a5a" />
                <stop offset=".32" stopColor="#e10000" />
                <stop offset=".72" stopColor="#a30000" />
                <stop offset="1" stopColor="#420000" />
              </linearGradient>
              <linearGradient id="au-shine" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#fff" stopOpacity=".95" />
                <stop offset="1" stopColor="#fff" stopOpacity="0" />
              </linearGradient>
              <radialGradient id="au-bulb-off" cx="35%" cy="30%" r="75%">
                <stop offset="0" stopColor="#ffffff" />
                <stop offset=".4" stopColor="#d9d9de" />
                <stop offset="1" stopColor="#8f8f99" />
              </radialGradient>
              <radialGradient id="au-bulb-on" cx="40%" cy="35%" r="70%">
                <stop offset="0" stopColor="#ffffff" />
                <stop offset=".35" stopColor="#fff3a0" />
                <stop offset="1" stopColor="#ffc928" />
              </radialGradient>
              <radialGradient id="au-under-off" cx="50%" cy="40%" r="60%">
                <stop offset="0" stopColor="#6d6d76" />
                <stop offset="1" stopColor="#2a2a2f" />
              </radialGradient>
              <radialGradient id="au-under-on" cx="50%" cy="40%" r="60%">
                <stop offset="0" stopColor="#fff6c0" />
                <stop offset="1" stopColor="#ff9d1f" />
              </radialGradient>
            </defs>

            {/* pearl cord */}
            <rect x="76" y="-400" width="8" height="456" fill="url(#au-pearls)" />

            {/* cap */}
            <rect x="70" y="50" width="20" height="12" rx="4" fill="url(#au-pearl)" />
            <rect x="72" y="52" width="6" height="8" rx="3" fill="#fff" opacity=".7" />

            {/* bulb glow + bulb (behind shade) */}
            <circle className="au-glow" cx="80" cy="124" r="26" fill="#ffd84a" />
            <circle className="au-x au-when-off" cx="80" cy="124" r="12" fill="url(#au-bulb-off)" />
            <circle className="au-x au-when-on" cx="80" cy="124" r="12" fill="url(#au-bulb-on)" />
            <ellipse cx="76" cy="120" rx="3.4" ry="2.4" fill="#fff" opacity=".9" />

            {/* underside of shade */}
            <ellipse className="au-x au-when-off" cx="80" cy="111" rx="46" ry="8" fill="url(#au-under-off)" />
            <ellipse className="au-x au-when-on" cx="80" cy="111" rx="46" ry="8" fill="url(#au-under-on)" />

            {/* shade, white (off) */}
            <g className="au-x au-when-off">
              <path d="M62 60H98L126 112Q80 124 34 112Z" fill="url(#au-white)" />
              <path d="M67 66L45 107" stroke="url(#au-shine)" strokeWidth="5" strokeLinecap="round" opacity=".95" />
              <path d="M34 112Q80 124 126 112" fill="none" stroke="#fff" strokeOpacity=".8" strokeWidth="1.4" />
            </g>

            {/* shade, red (on) */}
            <g className="au-x au-when-on">
              <path d="M62 60H98L126 112Q80 124 34 112Z" fill="url(#au-redg)" />
              <path d="M67 66L45 107" stroke="url(#au-shine)" strokeWidth="5" strokeLinecap="round" opacity=".8" />
              <path d="M34 112Q80 124 126 112" fill="none" stroke="#ff9a9a" strokeOpacity=".8" strokeWidth="1.4" />
            </g>
            <ellipse cx="72" cy="63" rx="6" ry="1.6" fill="#fff" opacity=".6" />

            {/* pearl pull chain */}
            <g className={`au-chain ${pulled ? "pulled" : ""}`}>
              {[118, 124.5, 131, 137.5, 144, 150.5].map((y) => (
                <circle key={y} cx="112" cy={y} r="3.2" fill="url(#au-pearl)" />
              ))}
              <circle cx="112" cy="160" r="6" fill="url(#au-pearl)" />
              <circle cx="110" cy="158" r="1.8" fill="#fff" opacity=".95" />
            </g>
          </svg>
        </button>
      </div>
      <div className="au-hint">{lampOn ? "PULL TO DIM" : "PULL TO LIGHT"}</div>

      <form className="au-card" onSubmit={submit}>
        <div className={`au-toggle ${mode === "register" ? "reg" : ""}`} role="tablist">
          <span className="au-ind" aria-hidden="true" />
          <button type="button" role="tab" aria-selected={mode === "login"} className={`au-tab ${mode === "login" ? "active" : ""}`} onClick={() => switchMode("login")}>
            LOGIN
          </button>
          <button type="button" role="tab" aria-selected={mode === "register"} className={`au-tab ${mode === "register" ? "active" : ""}`} onClick={() => switchMode("register")}>
            CREATE ACCOUNT
          </button>
        </div>

        <h2 className="au-title">{mode === "login" ? "Welcome back" : "Join us"}</h2>
        <p className="au-sub">{mode === "login" ? "LOG IN TO CONTINUE" : "CREATE YOUR ACCOUNT"}</p>

        {mode === "register" && (
          <div className="au-row">
            <input className="au-input" placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} required />
            <input className="au-input" placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)} required />
          </div>
        )}
        <input className="au-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input className="au-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />

        {error && <p className="au-error">{error}</p>}
        {note && <p className="au-note">{note}</p>}

        <button className="au-btn" disabled={busy}>
          {busy ? "ONE MOMENT..." : mode === "login" ? "LOG IN" : "CREATE ACCOUNT"}
        </button>
        <button type="button" className="au-btn ghost" onClick={() => dispatch(loginWithGoogle())} disabled={busy}>
          CONTINUE WITH GOOGLE
        </button>

        <div className="au-links">
          <button type="button" className="au-link" onClick={() => switchMode(mode === "login" ? "register" : "login")}>
            {mode === "login" ? "NEW HERE? SIGN UP" : "HAVE AN ACCOUNT? LOG IN"}
          </button>
          {mode === "login" && (
            <button type="button" className="au-link" onClick={forgot}>
              FORGOT PASSWORD?
            </button>
          )}
        </div>
      </form>
    </div>
  );
}